import type { TargetedMouseEvent } from 'preact';

import type { TemplateFunction, TemplateResult } from 'Components/types';
import { toggle } from 'Core/actions/request';
import { useDispatch } from 'Core/store';
import type { FacetValueFull } from 'Models/facetValue';
import { DisplayMode } from 'Modules/serverApi/types';
import { cloneSafe } from 'Utils/components';
import { isTruthy } from 'Utils/guards';
import { getRangeFiniteness, getRangeFromValue, type MinMax } from 'Utils/ranged';

interface Props {
  template: TemplateFunction<SimpleValueTemplateParams>;
  value: FacetValueFull;
}

export type SimpleValueTemplateParams = FacetValueFull & {
  facetDivClass: string;
  range: MinMax;
  isCustomRange: boolean;
} & ReturnType<typeof getRangeFiniteness>;

export default function SimpleValue({ template, value }: Props): TemplateResult {
  const dispatch = useDispatch();

  const { displayMode, isUnique, field, value: facetValue, term, isSelected, treeLevel } = value;

  const isRanged = displayMode === DisplayMode.Slider || displayMode === DisplayMode.Ranges;
  const range = isRanged ? getRangeFromValue(facetValue, term) : (['', ''] as MinMax);
  const rangeFiniteness = isRanged
    ? getRangeFiniteness(range)
    : { finite: false, toPosInf: false, toNegInf: false };

  const facetDivClass = [isSelected && 'selected', treeLevel && `cm_padding-${treeLevel}`]
    .filter(isTruthy)
    .join(' ');

  const params = {
    ...value,
    facetDivClass,
    range,
    isCustomRange: isUnique,
    ...rangeFiniteness,
  };

  const component = template.call(params);
  return cloneSafe(component, null, {
    onClick: (e) => {
      if (!onClickPrevented(e, component)) {
        dispatch(
          toggle(
            { field, term },
            { isSingleValue: displayMode === DisplayMode.Radio, mayDiscardValue: true },
          ),
        );
      }
    },
  });
}

function onClickPrevented(event: TargetedMouseEvent<HTMLElement>, component: TemplateResult) {
  const onClick = component?.props.onClick;
  if (typeof onClick === 'function') {
    onClick(event);
    return event.defaultPrevented;
  }
  return false;
}
